import React from 'react';
import type { ElementType } from 'react';

import type { Fragment, Node as ProseMirrorNode } from '@atlaskit/editor-prosemirror/model';
import type { HeadingAnchorLinksProps } from '@atlaskit/renderer/types';
import type { ReactSerializerInit } from '@atlaskit/renderer/react';
import ReactSerializer from '@atlaskit/renderer/react';
import { nodeToReact, toReact } from '@atlaskit/renderer/nodes';

import { IncrementalHeightEstimator } from '../../utils/node-estimated-height.ts';
import { NullRenderer } from '../progressive/NullRenderer.tsx';

import { getBatchIndex } from './getBatchIndex.ts';
import { LightNodeRenderer } from './LightNodeRenderer.tsx';
import { LightRootRenderer } from './LightRootRenderer.tsx';
import { LightSubtree } from './LightSubtree.tsx';
import { LightSubtreeScheduler } from './LightSubtreeScheduler.ts';
import { lightNodeComponents } from './LightADFNodeComponents.tsx';
import type { LightNodeName } from './LightADFNodeComponents.tsx';
import { FullBatchSerializer } from './FullBatchSerializer.ts';
import type { FullNodeRecords } from './FullBatchSerializer.ts';
import { planLightBatches } from './lightBatchEligibility.ts';
import { getTableBatchEnds } from './tableBatchEnds.ts';
import type { LightBatchPlan } from './lightBatchEligibility.ts';
import type { LightTableControls } from './nodes/table/lightTableTriggers.ts';
import { TableRow } from './nodes/table/TableRow.tsx';

// Viewport-height reference from the fe_performance analysis:
// "Use 2,160 px as our viewport-height assumption: it covers 99% of recorded screen heights
// across 18.1 million production Confluence initial View Page loads in fe_performance
// from 19–25 September 2026 UTC."
const BATCH_HEIGHT = 2160;

type PreloadableTarget = ElementType & { preload: () => unknown };

function isPreloadable(target?: ElementType): target is PreloadableTarget {
	return Boolean(
		target &&
		typeof target !== 'string' &&
		'preload' in target &&
		typeof target.preload === 'function',
	);
}

function prepareFullTargets(targets?: Set<PreloadableTarget>): (() => Promise<void>) | undefined {
	if (!targets?.size) {
		return undefined;
	}
	return async () => {
		await Promise.all([...targets].map((component) => component.preload()));
	};
}

type LightRenderingState = {
	plan: LightBatchPlan;
	scheduler: LightSubtreeScheduler;
	records: FullNodeRecords;
	// Set while a light top-level block serializes; its descendants serialize inside that call.
	currentFullTargets?: Set<PreloadableTarget>;
	currentSubtreeControls?: LightTableControls;
};

type SerializerNodeProps = Record<string, unknown> & {
	nodeType?: string;
	headingId?: string;
};

type SerializerParentInfo = {
	parentIsIncompleteTask: boolean;
	path: ProseMirrorNode[];
	pos: number;
};

// Platform's `Renderer` passes these defaults as `nodeComponents` too. Only a product's own components
// count: the full serializers add the defaults back themselves.
function withoutDefaultNodeComponents(init: ReactSerializerInit): ReactSerializerInit {
	if (!init.nodeComponents) {
		return init;
	}
	const nodeComponents = Object.fromEntries(
		Object.entries(init.nodeComponents).filter(
			([name, component]) => component !== nodeToReact[name],
		),
	);
	return { ...init, nodeComponents };
}

export class LightNodeSerializer extends ReactSerializer {
	private activeHeadingBatchIndex: number | undefined = undefined;
	private activeHeadingRenderIndex: number | undefined = undefined;
	private activeHeadingId: ReturnType<typeof getActiveHeadingId>;

	private maxRenderIndex: number = 0;

	private onlySerializeFirstBatch: boolean;
	private onBatchRendered?: (done: boolean) => void;
	// Indices ending each height-based batch; [80, 150] means batches are [0, 80) and [80, 150).
	private batchEndIndices: number[] = [];
	private lastBatchHeight = 0;
	private heightEstimator = new IncrementalHeightEstimator();
	private init: ReactSerializerInit;
	private lightRendering?: LightRenderingState;
	// Set while a full top-level table that spans batches serializes.
	private tableBatchEndRows?: ReadonlySet<ProseMirrorNode>;
	// Set while a row outside the first batch serializes: its cells stay, their content does not.
	private skipsTableRowContent = false;
	private readonly allowVirtualizeTables: boolean;

	constructor(
		init: ReactSerializerInit,
		onlySerializeFirstBatch: boolean = false,
		onBatchRendered?: (done: boolean) => void,
		private options: { allowLightRendering?: boolean; allowVirtualizeTables?: boolean } = {},
	) {
		const activeHeadingId = getActiveHeadingId(init.allowHeadingAnchorLinks);

		// Linked headings need IDs before their batch mounts.
		if (activeHeadingId && init.disableHeadingIDs) {
			init = {
				...init,
				disableHeadingIDs: false,
			};
		}

		super(init);

		this.activeHeadingId = activeHeadingId;
		this.onlySerializeFirstBatch = onlySerializeFirstBatch;
		this.onBatchRendered = onBatchRendered;
		this.init = withoutDefaultNodeComponents(init);
		this.allowVirtualizeTables = Boolean(
			options.allowLightRendering && options.allowVirtualizeTables,
		);
	}

	private resetProgressiveState() {
		this.activeHeadingBatchIndex = undefined;
		this.activeHeadingRenderIndex = undefined;

		this.maxRenderIndex = 0;
		this.batchEndIndices = [];
		this.lastBatchHeight = 0;
		this.heightEstimator = new IncrementalHeightEstimator();
	}

	serializeFragment(
		fragment: Fragment,
		props?: SerializerNodeProps,
		target?: ElementType,
		key?: string,
		parentInfo?: SerializerParentInfo,
	): JSX.Element | null {
		// Only the root call has no key.
		if (key === undefined) {
			return this.serializeRoot(fragment, props, target);
		}
		const renderIndex = this.maxRenderIndex++;
		const depth = parentInfo?.path.length;
		if (parentInfo && depth === 1) {
			return this.serializeTopLevel(fragment, props, target, key, parentInfo, renderIndex);
		}
		if (parentInfo && depth === 2 && this.tableBatchEndRows) {
			return this.serializeTableRow(fragment, props, target, key, parentInfo, renderIndex);
		}
		if (depth === 4 && this.skipsTableRowContent) {
			// A skipped row keeps its cells but not their content.
			return <NullRenderer key={key} />;
		}
		return this.serializeNode(fragment, props, target, key, parentInfo, renderIndex);
	}

	private serializeRoot(
		fragment: Fragment,
		props: SerializerNodeProps | undefined,
		target: ElementType | undefined,
	): JSX.Element | null {
		this.resetProgressiveState();
		this.lightRendering =
			this.options.allowLightRendering && !this.onlySerializeFirstBatch && !this.activeHeadingId
				? {
						plan: planLightBatches(fragment, this.init, BATCH_HEIGHT, {
							allowVirtualizeTables: this.allowVirtualizeTables,
						}),
						scheduler: new LightSubtreeScheduler(),
						records: new Map(),
					}
				: undefined;

		const element = this.serializeNode(
			fragment,
			props,
			target,
			undefined,
			undefined,
			this.maxRenderIndex++,
		);
		if (!element) {
			return element;
		}

		if (this.batchEndIndices[this.batchEndIndices.length - 1] !== this.maxRenderIndex) {
			this.batchEndIndices.push(this.maxRenderIndex);
		}
		return (
			<LightRootRenderer
				batchEndIndices={this.batchEndIndices}
				onBatchRendered={this.onBatchRendered}
				targetBatchIndex={this.activeHeadingBatchIndex}
				scheduler={this.lightRendering?.scheduler}
			>
				{element}
			</LightRootRenderer>
		);
	}

	private shouldSkipSerialization(renderIndex: number) {
		return (
			this.onlySerializeFirstBatch &&
			// Skip serialization of anything beyond the first batch:
			renderIndex >= this.getFirstBatchEndIndex() &&
			// This optimization cannot be used when rendering from a linked anchor that's not in the first batch:
			(!this.activeHeadingId || this.activeHeadingBatchIndex === 0)
		);
	}

	private serializeTopLevel(
		fragment: Fragment,
		props: SerializerNodeProps | undefined,
		target: ElementType | undefined,
		key: string,
		parentInfo: SerializerParentInfo,
		renderIndex: number,
	): JSX.Element | null {
		if (this.shouldSkipSerialization(renderIndex)) {
			// Node components expect their children to remain React elements.
			return <NullRenderer key={key} />;
		}

		const { pos } = parentInfo;
		const lightRendering = this.lightRendering;
		const plan = lightRendering?.plan;
		// Descendants inherit this block's eligibility, never a sibling's fallback.
		const fullTargets =
			plan?.batchByPosition.has(pos) && !plan.fallbackByPosition.has(pos)
				? new Set<PreloadableTarget>()
				: undefined;
		if (lightRendering) {
			lightRendering.currentFullTargets = fullTargets;
			lightRendering.currentSubtreeControls = fullTargets
				? getSubtreeControls(lightRendering.scheduler, pos)
				: undefined;
		}

		const node = parentInfo.path[0];
		if (node.type.name === 'table' && !fullTargets) {
			// Light rendering plans the row cuts up front; otherwise they follow the running estimate.
			this.tableBatchEndRows = plan
				? plan.tableBatchEndRows.get(node)
				: this.estimateTableBatchEnds(node);
		}
		const splitsTable = Boolean(this.tableBatchEndRows);
		const headingRenderIndexBefore = this.activeHeadingRenderIndex;
		let element = this.serializeNode(fragment, props, target, key, parentInfo, renderIndex);
		this.tableBatchEndRows = undefined;
		this.maybeEndBatch(pos);
		if (!element) {
			return element;
		}
		// A split table shows with a linked heading in its rows, even before the table's first batch.
		const headingRenderIndex =
			splitsTable && this.activeHeadingRenderIndex !== headingRenderIndexBefore
				? this.activeHeadingRenderIndex
				: undefined;
		const visibleFromIndex = headingRenderIndex ?? renderIndex;

		if (lightRendering && fullTargets) {
			element = (
				<LightSubtree
					scheduler={lightRendering.scheduler}
					resource={{
						subtreeId: pos,
						node,
						light: element,
						prepareFull: prepareFullTargets(fullTargets),
						serializeFull: () =>
							new FullBatchSerializer(
								{ ...this.init, startPos: pos },
								lightRendering.records,
							).serializeFragment(fragment, props, target, key, parentInfo),
					}}
				/>
			);
		}
		return (
			<LightNodeRenderer key={key} renderIndex={visibleFromIndex}>
				{element}
			</LightNodeRenderer>
		);
	}

	/** A row of a full top-level table whose rows end height batches. */
	private serializeTableRow(
		fragment: Fragment,
		props: SerializerNodeProps | undefined,
		target: ElementType | undefined,
		key: string,
		parentInfo: SerializerParentInfo,
		renderIndex: number,
	): JSX.Element | null {
		const row = parentInfo.path[1];
		// The header row stays with the start of its table, and table components check its type.
		const isHeaderRow = row === parentInfo.path[0].firstChild;
		// Platform's table matches each row's rendered cells to its ADF, so a skipped row keeps them.
		this.skipsTableRowContent = !isHeaderRow && this.shouldSkipSerialization(renderIndex);
		const element = this.serializeNode(fragment, props, target, key, parentInfo, renderIndex);
		this.skipsTableRowContent = false;
		if (this.tableBatchEndRows?.has(row)) {
			this.batchEndIndices.push(this.maxRenderIndex);
		}
		if (!element || isHeaderRow) {
			return element;
		}
		return (
			<LightNodeRenderer key={key} renderIndex={renderIndex}>
				{element}
			</LightNodeRenderer>
		);
	}

	private serializeNode(
		fragment: Fragment,
		props: SerializerNodeProps | undefined,
		target: ElementType | undefined,
		key: string | undefined,
		parentInfo: SerializerParentInfo | undefined,
		renderIndex: number,
	): JSX.Element | null {
		let element: JSX.Element | null;
		if (this.lightRendering) {
			element = this.serializeLightNode(
				this.lightRendering,
				fragment,
				props,
				target,
				key,
				parentInfo,
			);
		} else {
			this.heightEstimator.enterNode();
			element = super.serializeFragment(fragment, props, target, key, parentInfo);
			this.heightEstimator.leaveNode(parentInfo?.path);
		}
		if (element && this.activeHeadingId && this.activeHeadingBatchIndex === undefined) {
			this.maybeSetActiveHeadingBatchIndex(props, renderIndex);
		}
		return element;
	}

	/** Inside a light top-level block, swap in light components and record what the upgrade needs. */
	private serializeLightNode(
		lightRendering: LightRenderingState,
		fragment: Fragment,
		props: SerializerNodeProps | undefined,
		target: ElementType | undefined,
		key: string | undefined,
		parentInfo: SerializerParentInfo | undefined,
	): JSX.Element | null {
		const fullTargets = lightRendering.currentFullTargets;
		const node = parentInfo?.path[parentInfo.path.length - 1];
		if (!fullTargets || !parentInfo || !node) {
			return super.serializeFragment(fragment, props, target, key, parentInfo);
		}
		const textRow = lightRendering.plan.textRows.get(node);
		if (textRow) {
			// The row's content is serialized on activation; preload the full components it will use.
			for (const contentNode of textRow.contentNodes) {
				const contentTarget = toReact(
					contentNode,
					{ allowWindowedCodeBlock: this.init.allowWindowedCodeBlock },
					this.init.nodeComponents,
				);
				if (isPreloadable(contentTarget)) {
					fullTargets.add(contentTarget);
				}
			}
			return <TableRow key={key} textRow={textRow} />;
		}
		if (isPreloadable(target)) {
			fullTargets.add(target);
		}
		// Include depth: a parent and its first descendant can share Platform's raw position.
		lightRendering.records.set(`${parentInfo.pos}:${parentInfo.path.length}`, {
			1: props,
			2: target,
		});
		// Inside a column row, the row and its cells stay light; everything in a cell is real.
		const columnRowDepth = parentInfo.path.findIndex((ancestor) =>
			lightRendering.plan.columnRows.has(ancestor),
		);
		const isColumnRowContent =
			columnRowDepth !== -1 && parentInfo.path.length - 1 > columnRowDepth + 1;
		if (isColumnRowContent) {
			return super.serializeFragment(fragment, props, target, key, parentInfo);
		}
		const lightProps =
			node.type.name === 'table'
				? { ...props, subtreeControls: lightRendering.currentSubtreeControls }
				: props;
		return super.serializeFragment(
			fragment,
			lightProps,
			lightNodeComponents[node.type.name as LightNodeName],
			key,
			parentInfo,
		);
	}

	private estimateTableBatchEnds(table: ProseMirrorNode): ReadonlySet<ProseMirrorNode> | undefined {
		const { rows, lastBatchHeight } = getTableBatchEnds(
			table,
			this.heightEstimator.height,
			this.lastBatchHeight,
			BATCH_HEIGHT,
		);
		if (!rows.size) {
			return undefined;
		}
		this.lastBatchHeight = lastBatchHeight;
		return rows;
	}

	private maybeEndBatch(pos: number) {
		if (this.lightRendering) {
			// Consume the preflight boundaries instead of estimating heights again.
			if (this.lightRendering.plan.batchEndPositions.has(pos)) {
				this.batchEndIndices.push(this.maxRenderIndex);
			}
			return;
		}
		const { height } = this.heightEstimator;
		if (height - this.lastBatchHeight >= BATCH_HEIGHT) {
			// Keep the crossing top-level block intact, with the same boundary during SSR and hydration.
			this.batchEndIndices.push(this.maxRenderIndex);
			this.lastBatchHeight = height;
		}
	}

	private getFirstBatchEndIndex() {
		return this.batchEndIndices[0] ?? Infinity;
	}

	private maybeSetActiveHeadingBatchIndex(
		props: SerializerNodeProps | undefined,
		renderIndex: number,
	) {
		if (props?.nodeType === 'heading') {
			const { headingId } = props;
			if (headingId && headingId === this.activeHeadingId) {
				this.activeHeadingBatchIndex = getBatchIndex(renderIndex, this.batchEndIndices);
				this.activeHeadingRenderIndex = renderIndex;
			}
		}
	}
}

// Light tables force their subtree's upgrade for copy and pointer interactions.
function getSubtreeControls(
	scheduler: LightSubtreeScheduler,
	subtreeId: number,
): LightTableControls {
	return {
		activateNow: () => scheduler.activateNow(subtreeId),
		isPreloaded: () => scheduler.isPreloaded(subtreeId),
		isWhollySelected: (selection) => scheduler.isWhollySelected(subtreeId, selection),
		prepareNow: () => scheduler.prepareNow(subtreeId),
	};
}

function getActiveHeadingId(allowHeadingAnchorLinks?: HeadingAnchorLinksProps) {
	if (allowHeadingAnchorLinks && typeof allowHeadingAnchorLinks === 'object') {
		return allowHeadingAnchorLinks.activeHeadingId;
	}
}
